// Which product in the review dict a packed_unit belongs to.
//
// The review dict (`productsToReviewById`) is keyed by the product ids the operator
// must scan, which for a kit is the CHILD product ids — the kit parent is never a key.
// The unit, meanwhile, carries whatever `PackedUnitSerializer` emits:
//
//   id, stock_unit_id, order_line_id, packed_by_id, packed_at, sku, barcode,
//   product_name, description, imei
//
// Note what is NOT there: no `product_id`, no `upc`, no `product_code`. And `sku` is
// `PackedUnit#canonical_sku` = `order_line.sku` first, which on a kit line is the
// KIT's snapshot SKU, not the child's. So for a kit child the only code on the unit
// that can identify it is `barcode` (= the child `Product#bar_code`), and that column
// is nullable and defaults to "" in `create_product` / `create_kit`. A child
// identified by its `ProductCode.sku` therefore had NOTHING to match on: the count
// never restored on reload, and since BUGS-8257 made the count mandatory for every
// role, that is a dead end — the packed pieces cannot be re-scanned either
// (`unit_already_packed`).
//
// `stock_unit_id` is the identifier that always resolves, because
// `StockUnitWmsSerializer` exposes `id` AND `product_id` and the order payload
// carries the units under each line (the same field `requiredQuantities.js` counts).
// So it goes above the code ladder, which stays as the fallback for shapes that have
// no stock unit — the optimistic placeholders written at scan time, which carry
// `product_id` and the scanned `product_code` instead.

// A blank code is the ABSENCE of a code, never something to match on: `bar_code: ''`
// is the default both product factories write, and treating "" as a value would let a
// blank-on-both-sides unit resolve to an arbitrary blank-bar_code product.
const normalizeCode = value => (typeof value === 'string' ? value.trim() : value ? String(value) : '')

const matchesCode = (candidate, code) => {
  const normalized = normalizeCode(candidate)
  return normalized !== '' && normalized === code
}

export const resolveUnitProductId = ({ unit, productsToReviewById, orderLines } = {}) => {
  if (!unit) return null
  const productsById = productsToReviewById || {}
  const lines = orderLines || []

  // 1. The unit says which product it is, and the dict knows that product. True for
  //    the optimistic placeholders; a kit parent id would fail the dict check, which
  //    is the point.
  if (unit.product_id && productsById[unit.product_id]) return unit.product_id

  // 2. The stock unit behind it says so. Deterministic, and the ONLY thing that
  //    resolves a kit child whose product has a blank bar_code.
  if (unit.stock_unit_id) {
    for (const orderLine of lines) {
      const stockUnit = (orderLine?.stock_units || []).find(su => su?.id === unit.stock_unit_id)
      if (stockUnit?.product_id && productsById[stockUnit.product_id]) return stockUnit.product_id
    }
  }

  // 3. Any non-blank code the unit carries, against any non-blank code the product
  //    carries. `ProductCodeSerializer` emits only `sku` (no upc, no bar_code), so in
  //    practice this matches `product_codes[].sku` or the product's own `bar_code`;
  //    the other two comparisons are kept for payload shapes that do carry them.
  const codes = [unit.sku, unit.upc, unit.bar_code, unit.barcode, unit.product_code].map(normalizeCode).filter(code => code !== '')
  for (const code of codes) {
    const product = Object.values(productsById).find(
      candidate =>
        (candidate?.product_codes || []).some(pc => matchesCode(pc?.sku, code) || matchesCode(pc?.upc, code) || matchesCode(pc?.bar_code, code)) ||
        matchesCode(candidate?.bar_code, code),
    )
    if (product) return product.id
  }

  // 4. Last resort: the order line's own product, and ONLY when the dict knows it —
  //    never accepts a kit parent.
  if (unit.order_line_id) {
    const orderLine = lines.find(ol => ol?.id === unit.order_line_id)
    const orderLineProductId = orderLine?.product?.id
    if (orderLineProductId && productsById[orderLineProductId]) return orderLineProductId
  }

  return null
}
